import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconClose } from './icons';

/* A bottom sheet.
 *
 * Dismissible three ways — backdrop, Escape, and a real close button — because
 * a gloved thumb misses small targets and a swipe-only sheet is a trap. Focus
 * is moved in and restored on close so the keyboard path works too. */

export function Sheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<Element | null>(null);

  useEffect(() => {
    restoreTo.current = document.activeElement;
    ref.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);

    // The sheet owns the viewport while open; the list behind it must not
    // scroll away under your thumb.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      (restoreTo.current as HTMLElement | null)?.focus?.();
    };
  }, [onClose]);

  return createPortal(
    <div
      className="sheet-bd"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={ref}
      >
        <div className="sheet__grip" aria-hidden="true" />
        <div className="row" style={{ justifyContent: 'space-between', gap: 'var(--s-3)' }}>
          <h2 className="sheet__t" style={{ marginBottom: 0 }}>
            {title}
          </h2>
          <button
            type="button"
            className="btn btn--quiet"
            style={{ minHeight: 44, width: 44, padding: 0, borderRadius: 'var(--r-pill)' }}
            onClick={onClose}
            aria-label="Close"
          >
            <IconClose size={20} />
          </button>
        </div>
        <div style={{ marginTop: 'var(--s-4)' }}>{children}</div>
      </div>
    </div>,
    document.body,
  );
}
